/**
 * CallLeadPopupContext — global gate for the reusable "call a lead by phone"
 * popup. Any UI surface across the app (sidebar lead-info, lead profile,
 * deal card, leads list, etc.) can trigger an outbound phone call by
 * importing `useCallLeadPopup()` and invoking `openCall({...})`. The popup
 * itself is rendered as a sibling of the children so every consumer sees
 * the same modal — never multiple instances at once.
 *
 * Call lifecycle vs. WS impact:
 *   The popup mints a lead-scoped Voice token (`POST /voice/token` with the
 *   lead's id), so the backend creates a call session and the call IS
 *   session-linked — `agent:engaging` / `agent:disengaged` events flow to the
 *   lead's portal just like the dashboard CALL LEAD route. A numeric lead
 *   `id` is therefore REQUIRED to open the popup. See twilio.context.tsx's
 *   connect().
 */
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import CallLeadPopup from "@/components/call-lead/CallLeadPopup";
import ActiveCallIndicator from "@/components/call-lead/ActiveCallIndicator";
import CallRefreshGuard from "@/components/call-lead/CallRefreshGuard";

/**
 * Minimum lead identity needed to start a call.
 *
 * `id` is required: the Voice token is lead-scoped, so without a numeric lead
 * id there's no token and no call. `phoneNumber` is required too — a "Call"
 * CTA without a number to dial is a bug at the call site. Other fields are
 * display-only and degrade gracefully when absent.
 */
export type CallableLead = {
  id: number;
  name: string;
  /** Display label under the name, e.g. job title. Optional. */
  role?: string;
  /** Display label after the role (e.g. "Career Changer · Acme Holdings"). Optional. */
  company?: string;
  /** E.164 — what Twilio actually dials. */
  phoneNumber: string;
};

type CallLeadPopupContextValue = {
  isOpen: boolean;
  currentLead: CallableLead | null;
  openCall: (lead: CallableLead) => void;
  closeCall: () => void;
};

const CallLeadPopupContext = createContext<CallLeadPopupContextValue | null>(null);

export const CallLeadPopupProvider = ({ children }: PropsWithChildren) => {
  const [currentLead, setCurrentLead] = useState<CallableLead | null>(null);

  const openCall = useCallback((lead: CallableLead) => {
    setCurrentLead(lead);
  }, []);

  const closeCall = useCallback(() => {
    setCurrentLead(null);
  }, []);

  const value = useMemo<CallLeadPopupContextValue>(
    () => ({
      isOpen: currentLead !== null,
      currentLead,
      openCall,
      closeCall,
    }),
    [currentLead, openCall, closeCall],
  );

  return (
    <CallLeadPopupContext.Provider value={value}>
      {children}
      {/* Global call surfaces — all rendered as siblings of children so
          they overlay any route. The indicator self-gates on call state +
          popup visibility + current pathname; the refresh guard self-gates
          on call activity. The popup only renders when explicitly opened. */}
      <ActiveCallIndicator />
      <CallRefreshGuard />
      {currentLead && <CallLeadPopup lead={currentLead} onClose={closeCall} />}
    </CallLeadPopupContext.Provider>
  );
};

export const useCallLeadPopup = (): CallLeadPopupContextValue => {
  const ctx = useContext(CallLeadPopupContext);
  if (!ctx) {
    throw new Error("useCallLeadPopup must be used inside CallLeadPopupProvider");
  }
  return ctx;
};
